import { inject, Injectable } from '@angular/core';
import { formBuilder, select } from '@kinet-open-network/client/core/forms/ui';
import { confirmDialog, toast } from '@kinet-open-network/client/shared/ui';
import { ApiQuery } from '@kinet-open-network/shared/data-access/api';
import {
  DocumentContextType,
  DocumentState,
} from '@kinet-open-network/shared/data-access/model';
import { firstValueFrom } from 'rxjs';
import { CreateDocumentPayload, Document } from '../models/document.model';
import { DocumentsService } from './documents.service';

/** Max number of 2s polls while waiting for async document generation. */
const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

const generateId = () =>
  'crypto' in window
    ? crypto.randomUUID()
    : Math.random().toString(36).substring(2, 15);

/**
 * Reusable "print a filtered/sorted list" flow: shows the templates available
 * for a document context type, creates a document with the given query as its
 * context, waits for async generation to finish, then downloads the PDF.
 */
@Injectable({ providedIn: 'root' })
export class PrintService {
  private documentsService = inject(DocumentsService);

  async print(contextType: DocumentContextType, query: ApiQuery, name?: string) {
    const templates = await firstValueFrom(
      this.documentsService.getTemplatesOptions(contextType),
    );

    if (!templates.length) {
      toast({
        severity: 'warn',
        detail: 'Pre tento zoznam nie je k dispozícii žiadna šablóna na tlač',
      });
      return;
    }

    // Filters may be a builder instance (relies on toString()) — normalize to
    // a plain string so it survives JSON serialization in the request body.
    const normalizedQuery: ApiQuery = {
      ...query,
      filters: query.filters ? query.filters.toString() : undefined,
    };

    confirmDialog({
      header: 'Tlačiť',
      form: {
        model: { templateId: templates[0].id },
        fields: formBuilder([
          select('templateId', 'Šablóna')
            .options(templates.map((t) => ({ label: t.name, value: t.id })))
            .required(),
        ]),
        options: { submitText: 'Tlačiť' },
        onSubmit: (model) =>
          this.generateAndDownload(
            contextType,
            normalizedQuery,
            model.templateId,
            name,
          ),
      },
    });
  }

  private async generateAndDownload(
    contextType: DocumentContextType,
    query: ApiQuery,
    templateId: string,
    name?: string,
  ) {
    try {
      toast({ severity: 'info', detail: 'Dokument sa generuje' });

      const document = await firstValueFrom(
        this.documentsService.createDocument({
          parentType: contextType,
          parentId: generateId(),
          templateId,
          name,
          contextDef: { [contextType]: query },
        } satisfies CreateDocumentPayload),
      );

      const generated = await this.waitForGeneration(document.id);
      if (!generated) {
        toast({ severity: 'danger', detail: 'Generovanie dokumentu zlyhalo' });
        return;
      }
      if (generated.state === DocumentState.failed) {
        toast({
          severity: 'danger',
          detail: generated.errorMessage || 'Generovanie dokumentu zlyhalo',
        });
        return;
      }

      toast({ severity: 'success', detail: 'Dokument úspešne vygenerovaný' });

      await this.documentsService.downloadPdf(document.id, name);
    } catch {
      toast({ severity: 'danger', detail: 'Tlač zlyhala' });
    }
  }

  private async waitForGeneration(
    id: string,
    attempt = 0,
  ): Promise<Document | null> {
    if (attempt >= MAX_POLL_ATTEMPTS) {
      return null;
    }

    const document = await firstValueFrom(this.documentsService.findOne(id));
    if (
      document.state === DocumentState.generated ||
      document.state === DocumentState.failed
    ) {
      return document;
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    return this.waitForGeneration(id, attempt + 1);
  }
}
